import {
    CheckCircleRounded,
    ExpandMoreRounded,
    RadioButtonUncheckedRounded,
    RemoveCircleOutlineRounded,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import ISagaRequirementState from "models/ISagaRequirementState";
import ISagaState from "models/ISagaState";
import { observer } from "mobx-react";
import React from "react";
import theme from "Theme/AppTheme";
import {
    formatSagaName,
    sortRequirementsForDisplay,
    summarizeSaga,
} from "./SagaInspectorData";

function RequirementIcon({
    requirement,
}: {
    requirement: ISagaRequirementState;
}) {
    if (requirement.isNoLongerRequired) {
        return (
            <RemoveCircleOutlineRounded
                fontSize="small"
                sx={{ color: theme.palette.gray[400] }}
            />
        );
    }

    return requirement.isMet ? (
        <CheckCircleRounded
            fontSize="small"
            sx={{ color: theme.palette.success.main }}
        />
    ) : (
        <RadioButtonUncheckedRounded
            fontSize="small"
            sx={{ color: theme.palette.gray[400] }}
        />
    );
}

const RequirementRow = observer(function RequirementRow({
    requirement,
}: {
    requirement: ISagaRequirementState;
}) {
    const waived = requirement.isNoLongerRequired;

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "auto minmax(0, 1fr)",
                    md: "auto minmax(0, 1.2fr) minmax(0, 1fr)",
                },
                alignItems: "start",
                columnGap: 1.5,
                rowGap: 0.5,
                py: 1,
                px: 1.5,
                borderBottom: `1px solid ${theme.palette.gray[100]}`,
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", height: 24 }}>
                <RequirementIcon requirement={requirement} />
            </Box>

            <Stack sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        overflowWrap: "anywhere",
                        color: waived ? "text.secondary" : "text.primary",
                    }}
                >
                    {formatSagaName(requirement.id)}
                </Typography>
                {waived && requirement.noLongerRequiredReason && (
                    <Typography variant="caption" color="text.secondary">
                        No longer required — {requirement.noLongerRequiredReason}
                    </Typography>
                )}
            </Stack>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    gridColumn: { xs: "2", md: "auto" },
                    overflowWrap: "anywhere",
                    fontFamily: "monospace",
                }}
            >
                {requirement.value || "—"}
            </Typography>
        </Box>
    );
});

const SagaCard = observer(function SagaCard({
    saga,
    defaultExpanded,
}: {
    saga: ISagaState;
    defaultExpanded?: boolean;
}) {
    const summary = summarizeSaga(saga);
    const sortedRequirements = React.useMemo(
        () => sortRequirementsForDisplay(saga.sagaRequirementsState),
        [saga.sagaRequirementsState],
    );

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: `${theme.shape.borderRadius * 2}px !important`,
                "&:before": { display: "none" },
                overflow: "hidden",
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                sx={{
                    px: 2,
                    "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                    },
                }}
            >
                {/*
                 * The summary toggles the accordion on click, which would swallow a drag across the
                 * name as a collapse. Stopping mouse-down here keeps the name selectable for copying
                 * while the rest of the row still toggles.
                 */}
                <Typography
                    variant="subtitle2"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                        fontWeight: 600,
                        minWidth: 0,
                        userSelect: "text",
                        cursor: "text",
                    }}
                >
                    {formatSagaName(saga.sagaName)}
                </Typography>

                <Tooltip title={summary.statusDetail}>
                    <Chip
                        size="small"
                        label={summary.statusLabel}
                        sx={{
                            backgroundColor: summary.statusBackground,
                            color: summary.statusColor,
                            fontWeight: 500,
                        }}
                    />
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                {summary.requiredCount > 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap", mr: 1 }}
                    >
                        {summary.metCount} of {summary.requiredCount} met
                    </Typography>
                )}
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
                <LinearProgress
                    variant="determinate"
                    value={summary.progress}
                    sx={{
                        height: 4,
                        backgroundColor: theme.palette.gray[100],
                        "& .MuiLinearProgress-bar": {
                            backgroundColor: summary.statusColor,
                        },
                    }}
                />
                {sortedRequirements.length > 0 ? (
                    sortedRequirements.map((requirement) => (
                        <RequirementRow
                            key={requirement.id}
                            requirement={requirement}
                        />
                    ))
                ) : (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2 }}
                    >
                        {summary.status === "complete"
                            ? "This saga finished. Its tracking records are cleared on completion, so the individual requirements are no longer available."
                            : "No requirements are recorded for this saga."}
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
});

export default SagaCard;
